<template lang="pug">
#landing.animated.fadeIn
  // Top Header Section
  .top-wrapper(v-if="!whiteLabel")
    .top
      .logo
        img(:src="currentLogoPath" :alt="brand + ' Logo'")
  
  // Main Carousel Section
  section.slider-wrap
    .bg-landing(:style="{ backgroundImage: currentBackgroundImage }")
    .special-effects
      #cny-rain1(v-if="showSpecialEffects")
      #cny-rain2(v-if="showSpecialEffects")
    
    #main-carousel.carousel.slide(data-ride="carousel")
      ol.carousel-indicators
        li(
          v-for="(slide, index) in carouselSlides" 
          :key="slide.id"
          :class="{ active: index === 0 }"
          :data-target="'#main-carousel'"
          :data-slide-to="index"
        )
      
      .carousel-inner
        .carousel-item(
          v-for="(slide, index) in carouselSlides"
          :key="slide.id"
          :id="slide.id"
          :class="[slide.class, { active: index === 0 }]"
        )
          .banner-text
            h1(v-html="slide.title")
            p(v-if="slide.description" v-html="slide.description")
          .banner-image
            img.img-fluid.animate-image(
              v-if="slide.animateImage"
              :src="getImgUrl(slide.animateImage)"
              :alt="slide.alt"
              loading="lazy"
            )
            img.img-fluid(
              v-if="slide.staticImage"
              :src="getImgUrl(slide.staticImage)"
              :alt="slide.alt"
              loading="lazy"
            )
      
      a.carousel-control-prev(href="#main-carousel" role="button" data-slide="prev")
        span.carousel-control-prev-icon(aria-hidden="true")
        span.sr-only Previous
      a.carousel-control-next(href="#main-carousel" role="button" data-slide="next")
        span.carousel-control-next-icon(aria-hidden="true")
        span.sr-only Next
  
  // Login Modal
  loginModal(v-if="!whiteLabel")
  
  // Lobby Section
  section.lobby-wrap
    .inner-wrap
      .lobbyinner
        .row
          .col-12.mobile
            lobby-card(
              :title="$t('ui.sportsbook')"
              :description="$t('message.bannersb')"
              :image="getImgUrl('sportsbook_mobile.jpg?v=12')"
              :action-text="$t('ui.play_now')"
            )
          .col-6.col-md-4
            lobby-card(
              :title="$t('ui.livecasino')"
              :description="$t('message.bannerlc')"
              :image="getImgUrl('casino.jpg?v=12')"
              :action-text="$t('ui.play_now')"
            )
          .col-md-4.desktop
            lobby-card.sport(
              :title="$t('ui.sportsbook')"
              :description="$t('message.bannersb')"
              :image="getImgUrl('sportsbook.jpg?v=12')"
              :action-text="$t('ui.play_now')"
            )
          .col-6.col-md-4
            lobby-card(
              :title="$t('ui.slots')"
              :description="$t('message.bannersl')"
              :image="getImgUrl('games.jpg?v=12')"
              :action-text="$t('ui.play_now')"
            )
      
      .join-us(v-if="!whiteLabel")
        h1 {{ $t("message.join_us1") }}
        p {{ $t("message.join_us2") }}
        button.join-btn(data-toggle="modal" data-target="#login-modal") {{ $t("message.join_us3") }}
  
  // Features Section
  section.feature-wrap
    .deco1
      img(:src="getImgUrl('football.png?v=12')" alt="Football decoration" loading="lazy")
    .deco2
      img(:src="getImgUrl('footballplayer.png?v=12')" alt="Football player decoration" loading="lazy")
    
    .row.featureinner
      .col-12.col-md-6.feature-item(
        v-for="feature in features"
        :key="feature.id"
      )
        .icon
          img(:src="getImgUrl(feature.icon)" :alt="feature.title" loading="lazy")
        .feature-content
          h1 {{ feature.title }}
          p {{ feature.description }}
  
  // Footer Section
  footer.footer
    a.switch-desktop.mobile(v-if="!whiteLabel" :href="desktopUrl")
      img.desktop-icon(:src="getImgUrl('desktop-mode.svg?v=12')" :alt="'Switch to Desktop'")
      button {{ $t("ui.switch_to_desktop") }}
    
    .license
      .image-wrap(
        v-for="(license, index) in licenses"
        :key="index"
      )
        img(:src="getImgUrl(license)" :alt="'License ' + (index + 1)" loading="lazy")
    
    .tnc(v-if="!whiteLabel")
      .icon
        img(:src="getImgUrl('18plus.svg?v=12')" alt="18+ Icon" loading="lazy")
      .tnc-content
        h4 {{ $t("message.footer1a") }}
        p {{ $t("message.footer1b") }}
  
  // Tutorial Section
  .tutorial(v-if="showTutorial")
    .ios(v-if="isIOS")
      .d-flex.align-items-start
        img.icon(:src="appIconPath" :alt="brand + ' App Icon'")
        .flex-grow-1 {{ $t("ui.tutorial_tooltips") }}
      .link-wrapper.d-flex.justify-content-end
        a(href="javascript:void(0);" data-toggle='modal' data-target='#ios-homescreen-modal')
          .mr-4
            i.fas.fa-play
            | &nbsp;{{ $t("ui.tutorial") }}
        div(@click="closeTutorial") {{ $t("ui.dismiss") }}
</template>

<script>
import config from "@/config";
import loginModal from "@/components/mobile/loginModal";

// Optimized special effects with better performance
class SpecialEffectsManager {
  constructor() {
    this.animations = [];
    this.isActive = false;
  }

  init() {
    if (this.isActive) return;
    this.isActive = true;
    this.createRainEffect('cny-rain1', 30);
    this.createRainEffect('cny-rain2', 20);
  }

  createRainEffect(containerId, particleCount) {
    const container = document.getElementById(containerId);
    if (!container) return;

    // Use CSS animations instead of TweenMax for better performance
    container.style.perspective = '600px';
    
    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      particle.className = containerId.replace('cny-', 'cny-');
      particle.style.cssText = `
        position: absolute;
        width: 20px;
        height: 20px;
        background: radial-gradient(circle, rgba(255,215,0,0.8) 0%, rgba(255,165,0,0.4) 100%);
        border-radius: 50%;
        animation: rainFall ${this.getRandomDuration(6, 15)}s linear infinite;
        animation-delay: ${Math.random() * 5}s;
        left: ${Math.random() * 100}%;
        top: -20px;
        transform: translateZ(${Math.random() * 200 - 100}px);
      `;
      container.appendChild(particle);
    }
  }

  getRandomDuration(min, max) {
    return min + Math.random() * (max - min);
  }

  destroy() {
    this.animations.forEach(animation => animation.kill());
    this.animations = [];
    this.isActive = false;
  }
}

// Lobby Card Component
const LobbyCard = {
  props: ['title', 'description', 'image', 'actionText'],
  template: `
    <div class="lobby-item">
      <div class="card-flip" data-toggle="modal" data-target="#login-modal">
        <div class="front">
          <div class="image-wrap">
            <img :src="image" :alt="title" loading="lazy">
          </div>
          <div class="info">
            <h1>{{ title.toUpperCase() }}</h1>
          </div>
        </div>
        <div class="back">
          <h3>{{ title.toUpperCase() }}</h3>
          <p>{{ description }}</p>
          <div class="card-footer">
            <button class="btn" data-toggle="modal" data-target="#login-modal">
              {{ actionText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `
};

export default {
  name: 'WbetLanding',
  components: {
    loginModal,
    LobbyCard
  },
  data() {
    return {
      isStandalonePWA: false,
      isIOS: false,
      isAndroid: false,
      isWindows: false,
      countdown: null,
      hideCountdown: false,
      specialEffects: null,
      currentSlideIndex: 0
    }
  },
  computed: {
    // Logo management
    currentLogoPath() {
      if (this.cnyMode) return config.logoCNYPath;
      if (this.wcMode) return config.logoWCPath;
      if (this.xmasMode) return config.logoXMASPath;
      return config.logoPath;
    },
    
    // Special effects visibility
    showSpecialEffects() {
      return ['WBET', 'FOTBET'].includes(this.brand) && this.cnyMode;
    },
    
    // Tutorial visibility
    showTutorial() {
      return !this.isStandalonePWA && this.brand !== 'IBCBET' && !this.whiteLabel;
    },
    
    // App icon path
    appIconPath() {
      const iconMap = {
        'IBCBET': 'app_icon_gs.svg?v=12',
        'FOTBET': 'fotbet_icon_512x512.png',
        'UBETT': 'app_icon_ub.svg?v=12'
      };
      return this.getImgUrl(iconMap[this.brand] || 'app_icon.svg?v=12');
    },
    
    // Background image management
    currentBackgroundImage() {
      const slide = this.carouselSlides[this.currentSlideIndex];
      return slide ? `url(${this.getImgUrl(slide.backgroundImage)})` : '';
    },
    
    // Carousel slides data
    carouselSlides() {
      const slides = [
        {
          id: 'efight',
          class: 'efight',
          title: `${this.$t("message.banner6a")}<br>${this.$t("message.banner6b")}`,
          description: this.$t("message.banner6c"),
          animateImage: 'banner-08-2.png?v=12',
          staticImage: 'banner-08-1.png?v=12',
          backgroundImage: 'slider_esport_bg.jpg',
          alt: 'E-Fight Banner'
        },
        {
          id: 'vgames',
          class: 'vgames',
          title: `${this.$t("message.banner8a")}<br>${this.$t("message.banner8b")}`,
          description: `${this.$t("message.banner8c")}<br>${this.$t("message.banner8d")}`,
          animateImage: 'banner-12-2.png?v=12',
          staticImage: 'banner-12-1.png?v=12',
          backgroundImage: 'slider_racing_bg.jpg',
          alt: 'Virtual Games Banner'
        }
      ];

      if (this.esports2) {
        slides.push({
          id: 'efight2',
          class: 'efight2',
          title: `${this.$t("message.banner7a")}<br>${this.$t("message.banner7b")}`,
          description: `${this.$t("message.banner7c")}<br>${this.$t("message.banner7d")}`,
          animateImage: 'banner-09-2.png?v=12',
          staticImage: 'banner-09-1.png?v=12',
          backgroundImage: 'slider_tfgaming_bg.jpg',
          alt: 'E-Fight 2 Banner'
        });
      }

      slides.push(
        {
          id: 'sport',
          class: '',
          title: `${this.$t("message.banner1a")}<br>${this.$t("message.banner1b")}`,
          description: this.$t("message.banner1c"),
          animateImage: 'banner-01-2.png?v=12',
          staticImage: (this.brand === 'IBCBET' || this.brand === 'FOTBET') ? 'banner-01-1-gs.png?v=12' : 'banner-01-1.png?v=12',
          backgroundImage: 'slider_bg.jpg',
          alt: 'Sports Banner'
        },
        {
          id: 'casino',
          class: 'casino',
          title: `${this.$t("message.banner2a")}<br>${this.$t("message.banner2b")}`,
          description: this.$t("message.banner2c"),
          animateImage: 'banner-02-2.png?v=12',
          staticImage: 'banner-02-1.png?v=12',
          backgroundImage: 'slider_casino_bg.jpg',
          alt: 'Casino Banner'
        },
        {
          id: 'games',
          class: 'games',
          title: `${this.$t("message.banner3a")}<br>${this.$t("message.banner3b")}`,
          description: this.$t("message.banner3c"),
          animateImage: 'banner-03-2.png?v=12',
          staticImage: 'banner-03-1.png?v=12',
          backgroundImage: 'slider_games_bg.jpg',
          alt: 'Games Banner'
        },
        {
          id: 'live',
          class: 'live',
          title: `${this.$t("message.banner5a")}<br>${this.$t("message.banner5aa")}`,
          description: `${this.$t("message.banner5b")}<br>${this.$t("message.banner5c")}`,
          animateImage: 'banner-05-2.png?v=12',
          staticImage: (this.brand === 'IBCBET' || this.brand === 'FOTBET') ? 'banner-05-1-gs.png?v=12' : 'banner-05-1.png?v=12',
          backgroundImage: 'slider_live_bg.jpg',
          alt: 'Live Banner'
        }
      );

      return slides;
    },
    
    // Features data
    features() {
      return [
        {
          id: 1,
          icon: 'feature_icon1.svg?v=12',
          title: this.$t("message.icon1a"),
          description: this.$t("message.icon1b")
        },
        {
          id: 2,
          icon: 'feature_icon2.svg?v=12',
          title: this.$t("message.icon2a"),
          description: this.$t("message.icon2b")
        },
        {
          id: 3,
          icon: 'feature_icon3.svg?v=12',
          title: this.$t("message.icon3a"),
          description: this.$t("message.icon3b")
        },
        {
          id: 4,
          icon: 'feature_icon4.svg?v=12',
          title: this.$t("message.icon4a"),
          description: this.$t("message.icon4b")
        }
      ];
    },
    
    // License images
    licenses() {
      return [
        'license/01.png?v=12',
        'license/02.png?v=12',
        'license/03.png?v=12',
        'license/04.png?v=12',
        'license/05.png?v=12',
        'license/06.png?v=12',
        'license/07.png?v=12',
        'license/08.png?v=12'
      ];
    },
    
    // Config computed properties
    logoPath() {
      return config.logoPath;
    },
    cnyMode() {
      return config.cnyMode;
    },
    wcMode() {
      return config.wcMode;
    },
    xmasMode() {
      return config.xmasMode;
    },
    logoCNYPath() {
      return config.logoCNYPath;
    },
    brand() {
      return config.brand;
    },
    desktopUrl() {
      const domain = window.location.hostname;
      return `https://${domain.replace("mv2.", "dv2.")}?view=desktop`;
    },
    whiteLabel() {
      return config.whiteLabel == "1";
    },
    esports2() {
      return config.esports2 == "1";
    }
  },
  
  beforeMount() {
    this.detectMobileDevice();
  },
  
  beforeDestroy() {
    this.cleanup();
  },
  
  mounted() {
    this.initializeComponent();
  },
  
  methods: {
    // Initialize component
    initializeComponent() {
      if (this.$route.query.redirect) {
        this.$swal("Info", this.$t("message.login"), "info");
      }

      this.setupCarousel();
      this.initializeSpecialEffects();
    },
    
    // Setup carousel with optimized event handling
    setupCarousel() {
      this.$nextTick(() => {
        const carousel = document.getElementById('main-carousel');
        if (!carousel) return;

        // Use native event listeners instead of jQuery
        carousel.addEventListener('slide.bs.carousel', (e) => {
          this.handleSlideChange(e);
        });

        // Initialize Bootstrap carousel
        $(carousel).carousel({
          interval: 5000
        });
      });
    },
    
    // Handle slide change with optimized background switching
    handleSlideChange(event) {
      const slideId = event.relatedTarget.id;
      const slide = this.carouselSlides.find(s => s.id === slideId);
      
      if (slide) {
        this.currentSlideIndex = this.carouselSlides.findIndex(s => s.id === slideId);
        this.animateBackgroundChange();
      }
    },
    
    // Animate background change
    animateBackgroundChange() {
      const bgElement = document.querySelector('.bg-landing');
      if (!bgElement) return;

      bgElement.classList.add('fadeInSlider');
      setTimeout(() => {
        bgElement.classList.remove('fadeInSlider');
      }, 1000);
    },
    
    // Initialize special effects
    initializeSpecialEffects() {
      if (this.showSpecialEffects) {
        this.specialEffects = new SpecialEffectsManager();
        this.specialEffects.init();
      }
    },
    
    // Cleanup resources
    cleanup() {
      if (this.countdown) {
        clearInterval(this.countdown);
      }
      if (this.specialEffects) {
        this.specialEffects.destroy();
      }
    },
    
    // Optimized mobile device detection
    detectMobileDevice() {
      const userAgent = navigator.userAgent;
      const mobileDetectors = {
        iOS: /iPhone|iPad|iPod/i,
        Android: /Android/i,
        Windows: /IEMobile/i,
        BlackBerry: /BlackBerry/i,
        Opera: /Opera Mini/i
      };

      this.isIOS = mobileDetectors.iOS.test(userAgent);
      this.isAndroid = mobileDetectors.Android.test(userAgent);
      this.isWindows = mobileDetectors.Windows.test(userAgent);
    },
    
    // Image URL helper
    getImgUrl(filename) {
      return `${config.resourceImgUrl}/mobile/landing/${filename}`;
    },
    
    // Close tutorial
    closeTutorial() {
      this.isIOS = false;
    }
  }
};
</script>

<style scoped>
/* Add CSS animations for rain effect */
@keyframes rainFall {
  0% {
    transform: translateY(-100px) translateZ(0);
    opacity: 1;
  }
  100% {
    transform: translateY(100vh) translateZ(0);
    opacity: 0;
  }
}

.special-effects {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
}

.fadeInSlider {
  transition: background-image 1s ease-in-out;
}

/* Optimize image loading */
img[loading="lazy"] {
  opacity: 0;
  transition: opacity 0.3s;
}

img[loading="lazy"].loaded {
  opacity: 1;
}

/* Performance optimizations */
.carousel-item {
  will-change: transform;
}

.lobby-item {
  will-change: transform;
}

/* Reduce repaints */
.bg-landing {
  will-change: background-image;
}
</style>
